import { useAuthContext } from '@/auth/useAuthContext';
import { BookingDetail } from '@/components/BookingDetail';
import { BookingGroupRow } from '@/components/BookingGroupRow';
import { exportBookingsCsvUrl, getBookingGroup, listServices, searchBookings } from '@/lib/api';
import type { BookingSearchRow, ServiceCatalogueItem } from '@/lib/types';
import { useRealtime } from '@/lib/useRealtime';
import { useSpaceNoun } from '@/lib/useSpaceNoun';
import { Download, Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// Stable wrapper component so React doesn't lose the fragment key.
interface BookingGroupFragmentProps {
  groupId: string;
  groupRows: BookingSearchRow[];
  isExpanded: boolean;
  loadingDetail: boolean;
  groupDetail: { groupId: string; bookings: unknown[] } | null;
  onToggle: () => void;
  onClose: () => void;
  onDeleted: () => void;
}

function BookingGroupFragment({
  groupId,
  groupRows,
  isExpanded,
  loadingDetail,
  groupDetail,
  onToggle,
  onClose,
  onDeleted,
}: BookingGroupFragmentProps) {
  return (
    <>
      <BookingGroupRow rows={groupRows} expanded={isExpanded} onToggle={onToggle} />
      {isExpanded && (
        <tr>
          <td colSpan={8} className="p-0">
            <BookingDetail
              groupId={groupId}
              groupDetail={loadingDetail ? null : groupDetail}
              onClose={onClose}
              onDeleted={onDeleted}
            />
          </td>
        </tr>
      )}
    </>
  );
}

// Group BookingSearchRows by their bookingGroupId (or bookingId as fallback).
function groupByBookingGroupId(rows: BookingSearchRow[]): Map<string, BookingSearchRow[]> {
  const map = new Map<string, BookingSearchRow[]>();
  for (const row of rows) {
    const key = row.bookingGroupId ?? row.bookingId;
    const existing = map.get(key);
    if (existing) {
      existing.push(row);
    } else {
      map.set(key, [row]);
    }
  }
  return map;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'confirmed_deposit', label: 'Deposit paid' },
  { value: 'paid', label: 'Fully paid' },
  { value: 'checked_in', label: 'Checked in' },
  { value: 'checked_out', label: 'Checked out' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'no_show', label: 'No-show' },
];

const SPECIES_OPTIONS = [
  { value: '', label: 'All species' },
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
];

export function History() {
  const { state } = useAuthContext();
  const noun = useSpaceNoun();
  const { me } = state;
  const facilityId = me?.activeFacility?.id;
  const [searchParams] = useSearchParams();

  // Filters — pre-populated from URL params when navigating from suggestions.
  const [q, setQ] = useState('');
  const [species, setSpecies] = useState('');
  const [service, setService] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [status, setStatus] = useState(() => searchParams.get('status') ?? '');

  // Data
  const [rows, setRows] = useState<BookingSearchRow[]>([]);
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Expanded group
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupDetail, setGroupDetail] = useState<{
    groupId: string;
    // biome-ignore lint/suspicious/noExplicitAny: full group shape is TBD
    bookings: any[];
  } | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildFilters = useCallback(() => {
    return {
      ...(q ? { q } : {}),
      ...(species ? { species } : {}),
      ...(service ? { service } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(facilityId ? { facilityId } : {}),
      ...(status ? { status } : {}),
    };
  }, [q, species, service, from, to, facilityId, status]);

  const doSearch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const results = await searchBookings(buildFilters());
      setRows(results);
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [buildFilters]);

  // Debounced search on filter change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void doSearch();
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [doSearch]);

  // Load service catalogue for the filter dropdown
  useEffect(() => {
    listServices(facilityId)
      .then(setServices)
      .catch(() => setServices([]));
  }, [facilityId]);

  // Realtime: refetch on booking/service events for active facility
  const facilityIdRef = useRef(facilityId);
  facilityIdRef.current = facilityId;

  useRealtime(
    useCallback(
      (event) => {
        const isRelevant =
          event.type === 'BOOKING_CREATED' ||
          event.type === 'BOOKING_UPDATED' ||
          event.type === 'SERVICE_UPDATED';
        if (!isRelevant) return;
        if (facilityIdRef.current && event.facility_id !== facilityIdRef.current) return;
        void doSearch();
      },
      [doSearch],
    ),
  );

  async function handleRowExpand(groupId: string) {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setGroupDetail(null);
      return;
    }
    setExpandedGroupId(groupId);
    setGroupDetail(null);
    setLoadingDetail(true);
    try {
      const detail = await getBookingGroup(groupId);
      setGroupDetail(detail);
    } catch {
      setGroupDetail({ groupId, bookings: [] });
    } finally {
      setLoadingDetail(false);
    }
  }

  function handleExport() {
    const url = exportBookingsCsvUrl(buildFilters() as Record<string, string | undefined>);
    window.open(url, '_blank');
  }

  function handleDeleted() {
    setExpandedGroupId(null);
    setGroupDetail(null);
    void doSearch();
  }

  const grouped = groupByBookingGroupId(rows);
  const groupKeys = [...grouped.keys()];

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-medium tracking-tight">Booking history</h1>
        <button
          type="button"
          onClick={handleExport}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium hover:bg-secondary press"
          aria-label="Export CSV"
        >
          <Download className="size-4" strokeWidth={1.75} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* Search */}
        <div className="relative sm:col-span-2">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.75}
          />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Customer or pet name…"
            className="h-10 w-full rounded-md border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
            aria-label="Search"
          />
        </div>

        {/* Species */}
        <select
          value={species}
          onChange={(e) => setSpecies(e.target.value)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          aria-label="Filter by species"
        >
          {SPECIES_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Service */}
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          aria-label="Filter by service"
        >
          <option value="">All services</option>
          {services.map((svc) => (
            <option key={svc.id} value={svc.slug}>
              {svc.name}
            </option>
          ))}
        </select>

        {/* Date from */}
        <div>
          <label
            htmlFor="filter-from"
            className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            From
          </label>
          <input
            id="filter-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        {/* Date to */}
        <div>
          <label
            htmlFor="filter-to"
            className="mb-1 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
          >
            To
          </label>
          <input
            id="filter-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="num h-10 w-full rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </div>

        {/* Status */}
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* State: loading */}
      {loading && <div className="py-12 text-center text-sm text-muted-foreground">Searching…</div>}

      {/* State: error */}
      {error && !loading && (
        <div className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* State: empty */}
      {!loading && !error && rows.length === 0 && (
        <div className="rounded-xl border border-border bg-surface px-6 py-12 text-center">
          <p className="text-sm text-muted-foreground">No bookings found.</p>
        </div>
      )}

      {/* Results table */}
      {!loading && !error && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-border bg-secondary/40">
                <th className="w-6 py-2.5 pl-4 pr-2" />
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Customer
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Pet
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Check-in
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Checkout
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {noun.Singular}
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Status
                </th>
                <th className="py-2.5 px-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Services
                </th>
                <th className="py-2.5 px-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {groupKeys.map((groupId) => {
                const groupRows = grouped.get(groupId) ?? [];
                const isExpanded = expandedGroupId === groupId;
                return (
                  <BookingGroupFragment
                    key={groupId}
                    groupId={groupId}
                    groupRows={groupRows}
                    isExpanded={isExpanded}
                    loadingDetail={loadingDetail}
                    groupDetail={groupDetail}
                    onToggle={() => void handleRowExpand(groupId)}
                    onClose={() => {
                      setExpandedGroupId(null);
                      setGroupDetail(null);
                    }}
                    onDeleted={handleDeleted}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
